import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  IconButton, 
  Card, 
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  CameraAlt as CameraIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { addCourt, updateCourt } from '../../store/CourtSlice';
import courtService from '../../services/courtService';

const CourtForm = ({ court, onClose, isEdit = false }) => {
  const dispatch = useDispatch();
  
  const [formData, setFormData] = useState({
    courtName: '',
    hlsUrl: '',
    streamKey: '',
    cameras: [],
    sessions: []
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit && court) {
      setFormData({
        courtName: court.courtName || '',
        hlsUrl: court.hlsUrl || '',
        streamKey: court.streamKey || '',
        cameras: court.cameras || [],
        sessions: court.sessions || []
      });
    }
  }, [isEdit, court]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleAddCamera = () => {
    const newCamera = {
      cameraName: '',
      rtspUrl: '',
      active: true
    };
    
    setFormData(prev => ({
      ...prev,
      cameras: [...prev.cameras, newCamera]
    }));
  };

  const handleUpdateCamera = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      cameras: prev.cameras.map((camera, i) => 
        i === index ? { ...camera, [field]: value } : camera
      )
    }));
  };

  const handleRemoveCamera = (index) => {
    setFormData(prev => ({
      ...prev,
      cameras: prev.cameras.filter((_, i) => i !== index)
    }));
  };

  const handleAddSession = () => {
    const newSession = {
      sessionName: '',
      dayOfWeek: 'MONDAY',
      startTime: '09:00',
      endTime: '17:00',
      active: true
    };
    
    setFormData(prev => ({
      ...prev,
      sessions: [...prev.sessions, newSession]
    }));
  };

  const handleUpdateSession = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      sessions: prev.sessions.map((session, i) => 
        i === index ? { ...session, [field]: value } : session
      )
    }));
  };

  const handleRemoveSession = (index) => {
    setFormData(prev => ({
      ...prev,
      sessions: prev.sessions.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const validation = courtService.validateCourtData(formData);
    if (!validation.isValid) {
      setErrors({ general: validation.errors.join(', ') });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      if (isEdit) {
        await dispatch(updateCourt({ 
          courtId: court.courtId, 
          courtDto: formData 
        })).unwrap();
        toast.success('Court updated successfully');
      } else {
        await dispatch(addCourt(formData)).unwrap();
        toast.success('Court created successfully');
      }
      onClose();
    } catch (error) {
      setErrors({ 
        general: error.message || 'Failed to save court' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} className="space-y-6">
      {errors.general && (
        <Alert severity="error">{errors.general}</Alert>
      )}

      {/* Basic Court Information */}
      <Card>
        <CardContent>
          <Typography variant="h6" className="mb-4 flex items-center">
            <CameraIcon className="mr-2" />
            Basic Information
          </Typography>
          
          <Box className="space-y-4">
            <TextField
              fullWidth
              label="Court Name"
              value={formData.courtName}
              onChange={(e) => handleInputChange('courtName', e.target.value)}
              required
              error={!!errors.courtName}
              helperText={errors.courtName}
            />
            
            <TextField
              fullWidth
              label="HLS URL"
              value={formData.hlsUrl}
              onChange={(e) => handleInputChange('hlsUrl', e.target.value)}
              placeholder="https://example.com/stream.m3u8"
            />
            
            <TextField
              fullWidth
              label="Stream Key"
              value={formData.streamKey}
              onChange={(e) => handleInputChange('streamKey', e.target.value)}
              placeholder="Optional custom stream key"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Cameras Section */}
      <Card>
        <CardContent>
          <Box className="flex justify-between items-center mb-4">
            <Typography variant="h6" className="flex items-center">
              <CameraIcon className="mr-2" />
              Cameras
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddCamera}
              variant="outlined"
              size="small"
            >
              Add Camera
            </Button>
          </Box>

          {formData.cameras.length === 0 ? (
            <Typography variant="body2" className="text-gray-500 text-center py-4">
              No cameras added yet. Click "Add Camera" to get started.
            </Typography>
          ) : (
            <Box className="space-y-4">
              {formData.cameras.map((camera, index) => (
                <Card key={index} variant="outlined">
                  <CardContent className="p-4">
                    <Box className="flex justify-between items-start mb-3">
                      <Typography variant="subtitle2">
                        Camera {index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveCamera(index)}
                        className="text-red-500"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    
                    <Box className="space-y-3">
                      <TextField
                        fullWidth
                        label="Camera Name"
                        value={camera.cameraName}
                        onChange={(e) => handleUpdateCamera(index, 'cameraName', e.target.value)}
                        placeholder="e.g., Main Camera"
                        size="small"
                      />
                      
                      <TextField
                        fullWidth
                        label="RTSP URL"
                        value={camera.rtspUrl}
                        onChange={(e) => handleUpdateCamera(index, 'rtspUrl', e.target.value)}
                        placeholder="rtsp://username:password@ip:port/path"
                        size="small"
                      />
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={camera.active}
                            onChange={(e) => handleUpdateCamera(index, 'active', e.target.checked)}
                          />
                        }
                        label="Active"
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Sessions Section */}
      <Card>
        <CardContent>
          <Box className="flex justify-between items-center mb-4">
            <Typography variant="h6" className="flex items-center">
              <ScheduleIcon className="mr-2" />
              Court Sessions
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddSession}
              variant="outlined"
              size="small"
            >
              Add Session
            </Button>
          </Box>

          {formData.sessions.length === 0 ? (
            <Typography variant="body2" className="text-gray-500 text-center py-4">
              No sessions added yet. Click "Add Session" to get started.
            </Typography>
          ) : (
            <Box className="space-y-4">
              {formData.sessions.map((session, index) => (
                <Card key={index} variant="outlined">
                  <CardContent className="p-4">
                    <Box className="flex justify-between items-start mb-3">
                      <Typography variant="subtitle2">
                        Session {index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveSession(index)}
                        className="text-red-500"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    
                    <Box className="space-y-3">
                      <TextField
                        fullWidth
                        label="Session Name"
                        value={session.sessionName}
                        onChange={(e) => handleUpdateSession(index, 'sessionName', e.target.value)}
                        placeholder="e.g., Monday Morning"
                        size="small"
                      />
                      
                      <FormControl fullWidth size="small">
                        <InputLabel>Day of Week</InputLabel>
                        <Select
                          value={session.dayOfWeek}
                          onChange={(e) => handleUpdateSession(index, 'dayOfWeek', e.target.value)}
                          label="Day of Week"
                        >
                          {['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'].map((day) => (
                            <MenuItem key={day} value={day}>
                              {day}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        alignItems: { xs: 'stretch', sm: 'center' }
                      }}>
                        <TextField
                          label="Start Time"
                          type="time"
                          value={session.startTime}
                          onChange={(e) => handleUpdateSession(index, 'startTime', e.target.value)}
                          size="small"
                          InputLabelProps={{

                          }}
                          inputProps={{
                            step: 1, // 1 second
                          }}
                          sx={{ flex: 1 }}
                        />
                        
                        <TextField
                          label="End Time"
                          type="time"
                          value={session.endTime}
                          onChange={(e) => handleUpdateSession(index, 'endTime', e.target.value)}
                          size="small"
                          InputLabelProps={{

                          }}
                          inputProps={{
                            step: 1, // 1 second
                          }}
                          sx={{ flex: 1 }}
                        />
                      </Box>
                      
                      {/* Quick Time Presets */}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', alignSelf: 'center' }}>
                          Quick presets:
                        </Typography>
                        {[
                          { label: 'Morning (9-12)', start: '09:00', end: '12:00' },
                          { label: 'Afternoon (1-5)', start: '13:00', end: '17:00' },
                          { label: 'Evening (6-9)', start: '18:00', end: '21:00' },
                          { label: 'Full Day (9-5)', start: '09:00', end: '17:00' }
                        ].map((preset) => (
                          <Button
                            key={preset.label}
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              handleUpdateSession(index, 'startTime', preset.start);
                              handleUpdateSession(index, 'endTime', preset.end);
                            }}
                            sx={{
                              minWidth: 'auto',
                              px: 1,
                              py: 0.5,
                              fontSize: '0.75rem',
                              textTransform: 'none'
                            }}
                          >
                            {preset.label}
                          </Button>
                        ))}
                      </Box>
                      
                      <FormControlLabel
                        control={
                          <Switch
                            checked={session.active}
                            onChange={(e) => handleUpdateSession(index, 'active', e.target.checked)}
                          />
                        }
                        label="Active"
                      />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Form Actions */}
      <Box className="flex justify-end gap-3 pt-4">
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          className="bg-accent-teal hover:bg-accent-teal/90"
        >
          {loading ? 'Saving...' : (isEdit ? 'Update Court' : 'Create Court')}
        </Button>
      </Box>
    </Box>
  );
};

export default CourtForm;
